import {
  commerceFlag,
  commerceWriteSucceeded,
  commerceWriteRejected,
} from "../comercios/model/commerceModel";
import axiosInstance from "core/api/axiosInstance";

// Cache simple para evitar llamadas repetidas
const cache = {
  attributes: null,
  attributesTimestamp: null,
  addComercios: null,
  addComerciosTimestamp: null,
  terminales: new Map(), // Mapa de idCommerce -> {data, timestamp}
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene la lista de configuraciones de WhatsApp para comercios
 */
export const getCommerceWhatsAppService = async (options = {}) => {
  try {
    const { data } = await axiosInstance.get(
      "/api/automatization/jarvis/get-commerce-whatsapp",
      options.signal ? { signal: options.signal } : {}
    );

    if (!data || typeof data !== "object") {
      console.error("❌ Respuesta inválida de WhatsApp commerce:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    if (!Array.isArray(data.response))
      throw new Error("No se recibió una lista válida de configuraciones WhatsApp.");
    const whatsappArray = data.response;

    const formattedWhatsApp = whatsappArray.map((item) => ({
      idCommerce: item.idcommerce,
      idTerminal: item.idterminal,
      nombreComercio: item.namecommerce,
      grupoAlertaNOC: item.namegroupalertnoc || "Sin grupo",
      grupoComercio: item.namegroupcommerce || "Sin grupo",
      groupName: item.namegroupcommerce || "Sin grupo",
      workflow: item.nameworkflows,
      terminalName: item.terminalname || "Sin terminal",
      chatId: item.chatId ?? item.chatid ?? "",
      activo: commerceFlag(item.activo),
      pulso: commerceFlag(item.pulso),
    }));

    const sortedWhatsApp = formattedWhatsApp.sort((a, b) =>
      (a.nombreComercio || "").localeCompare(b.nombreComercio || "", undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

    return {
      success: true,
      data: sortedWhatsApp,
      total: sortedWhatsApp.length,
      message: data.msm || "Configuraciones de WhatsApp obtenidas exitosamente",
      rawData: whatsappArray,
    };
  } catch (error) {
    console.error("❌ Error al obtener configuraciones de WhatsApp:", error);

    let errorMessage = "Error al obtener la lista de configuraciones de WhatsApp";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        `Error ${error.response.status}: ${error.response.statusText}`;
      console.error(`📊 Detalles del error:`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
      console.error("🌐 Error de conexión:", error.request);
    } else {
      errorMessage = error.message;
      console.error("⚙️ Error de configuración:", error.message);
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      total: 0,
    };
  }
};

/**
 * Obtiene la lista de comercios disponibles para agregar a WhatsApp (con caché)
 */
export const getCommerceWhatsAppAddService = async (forceRefresh = false) => {
  // Verificar caché
  const now = Date.now();
  if (
    !forceRefresh &&
    cache.addComercios &&
    cache.addComerciosTimestamp &&
    now - cache.addComerciosTimestamp < CACHE_DURATION
  ) {
    return {
      success: true,
      data: cache.addComercios,
      total: cache.addComercios.length,
      message: "Comercios disponibles obtenidos de caché",
      fromCache: true,
    };
  }

  try {
    const { data } = await axiosInstance.get(
      "/api/automatization/jarvis/get-commerce-whatsapp-add"
    );

    if (!data || typeof data !== "object") {
      console.error("❌ Respuesta inválida de comercios add:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    const comerciosArray = Array.isArray(data.response) ? data.response : [];

    const formattedComercios = comerciosArray.map((comercio) => ({
      id: comercio.idcommerce,
      nombre: comercio.namecomercio,
    }));

    const sortedComercios = formattedComercios.sort((a, b) =>
      (a.nombre || "").localeCompare(b.nombre || "", undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

    // Guardar en caché
    cache.addComercios = sortedComercios;
    cache.addComerciosTimestamp = now;

    return {
      success: true,
      data: sortedComercios,
      total: sortedComercios.length,
      message: data.msm || "Comercios disponibles obtenidos exitosamente",
      rawData: comerciosArray,
    };
  } catch (error) {
    console.error("❌ Error al obtener comercios disponibles:", error);

    let errorMessage = "Error al obtener comercios disponibles";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        `Error ${error.response.status}: ${error.response.statusText}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      total: 0,
    };
  }
};

/**
 * Obtiene los atributos para configuraciones de WhatsApp (con caché)
 */
export const getCommerceWhatsAppAttributesService = async (forceRefresh = false) => {
  // Verificar caché
  const now = Date.now();
  if (
    !forceRefresh &&
    cache.attributes &&
    cache.attributesTimestamp &&
    now - cache.attributesTimestamp < CACHE_DURATION
  ) {
    return {
      success: true,
      data: cache.attributes,
      message: "Atributos obtenidos de caché",
      fromCache: true,
    };
  }

  try {
    const { data } = await axiosInstance.get(
      "/api/automatization/jarvis/get-commerce-whatsapp-attribute"
    );

    if (!data || typeof data !== "object") {
      console.error("❌ Respuesta inválida de atributos:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: null,
      };
    }

    // Guardar en caché
    cache.attributes = data.response || {};
    cache.attributesTimestamp = now;

    return {
      success: true,
      data: data.response || {},
      message: data.msm || "Atributos obtenidos exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener atributos de WhatsApp:", error);

    let errorMessage = "Error al obtener atributos";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        `Error ${error.response.status}: ${error.response.statusText}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Obtiene terminales disponibles para un comercio (con caché)
 * @param {number} idCommerce - ID del comercio
 */
export const getCommerceWhatsAppTerminalsService = async (idCommerce, forceRefresh = false) => {
  // Verificar caché por comercio
  const now = Date.now();
  const cacheKey = idCommerce.toString();
  const cachedData = cache.terminales.get(cacheKey);

  if (
    !forceRefresh &&
    cachedData &&
    cachedData.timestamp &&
    now - cachedData.timestamp < CACHE_DURATION
  ) {
    return {
      success: true,
      data: cachedData.data,
      total: cachedData.data.length,
      message: "Terminales obtenidos de caché",
      fromCache: true,
    };
  }

  try {
    const { data } = await axiosInstance.get(
      `/api/automatization/jarvis/get-commerce-whatsapp-terminals/${idCommerce}`
    );

    if (!data || typeof data !== "object") {
      console.error("❌ Respuesta inválida de terminales:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    const terminalesArray = Array.isArray(data.response) ? data.response : [];

    const formattedTerminales = terminalesArray.map((terminal) => ({
      id: terminal.idterminal,
      nombre: terminal.terminalname,
    }));

    // Guardar en caché
    cache.terminales.set(cacheKey, {
      data: formattedTerminales,
      timestamp: now,
    });

    return {
      success: true,
      data: formattedTerminales,
      total: formattedTerminales.length,
      message: data.msm || "Terminales obtenidos exitosamente",
      rawData: terminalesArray,
    };
  } catch (error) {
    console.error("❌ Error al obtener terminales:", error);

    let errorMessage = "Error al obtener terminales";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        `Error ${error.response.status}: ${error.response.statusText}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      total: 0,
    };
  }
};

/**
 * Limpia toda la caché
 */
export const clearWhatsAppCache = () => {
  cache.attributes = null;
  cache.attributesTimestamp = null;
  cache.addComercios = null;
  cache.addComerciosTimestamp = null;
  cache.terminales.clear();
};

/**
 * Obtiene configuraciones activas de WhatsApp
 */
export const getCommerceWhatsAppActivosService = async () => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const activos = result.data.filter((item) => item.activo === true);

    return {
      ...result,
      data: activos,
      total: activos.length,
      message: "Configuraciones activas de WhatsApp obtenidas exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener configuraciones activas de WhatsApp:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Obtiene configuraciones con pulso activo
 */
export const getCommerceWhatsAppConPulsoService = async () => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const conPulso = result.data.filter((item) => item.pulso === true);

    return {
      ...result,
      data: conPulso,
      total: conPulso.length,
      message: "Configuraciones con pulso activo obtenidas exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener configuraciones con pulso:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Busca configuraciones de WhatsApp por término
 * @param {string} searchTerm - Término de búsqueda
 */
export const buscarCommerceWhatsAppService = async (searchTerm) => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const terminoBusqueda = searchTerm.toLowerCase().trim();
    const filtrados = result.data.filter(
      (item) =>
        item.nombreComercio?.toLowerCase().includes(terminoBusqueda) ||
        item.grupoComercio?.toLowerCase().includes(terminoBusqueda) ||
        item.grupoAlertaNOC?.toLowerCase().includes(terminoBusqueda) ||
        item.workflow?.toLowerCase().includes(terminoBusqueda) ||
        item.terminalName?.toLowerCase().includes(terminoBusqueda) ||
        item.idCommerce?.toString().includes(terminoBusqueda) ||
        (item.idTerminal && item.idTerminal?.toString().includes(terminoBusqueda))
    );

    return {
      ...result,
      data: filtrados,
      total: filtrados.length,
      message: `Búsqueda de configuraciones WhatsApp con "${searchTerm}" completada`,
    };
  } catch (error) {
    console.error(
      `❌ Error al buscar configuraciones WhatsApp con término "${searchTerm}":`,
      error
    );
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Filtra configuraciones de WhatsApp por grupo NOC
 * @param {string} grupo - Nombre del grupo NOC
 */
export const filtrarCommerceWhatsAppPorGrupoNOCService = async (grupo) => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const filtrados = result.data.filter(
      (item) => item.grupoAlertaNOC?.toLowerCase() === grupo.toLowerCase()
    );

    return {
      ...result,
      data: filtrados,
      total: filtrados.length,
      message: `Configuraciones WhatsApp del grupo NOC "${grupo}" obtenidas exitosamente`,
    };
  } catch (error) {
    console.error(`❌ Error al filtrar por grupo NOC ${grupo}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Filtra configuraciones de WhatsApp por workflow
 * @param {string} workflow - Nombre del workflow
 */
export const filtrarCommerceWhatsAppPorWorkflowService = async (workflow) => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const filtrados = result.data.filter(
      (item) => item.workflow?.toLowerCase() === workflow.toLowerCase()
    );

    return {
      ...result,
      data: filtrados,
      total: filtrados.length,
      message: `Configuraciones WhatsApp del workflow "${workflow}" obtenidas exitosamente`,
    };
  } catch (error) {
    console.error(`❌ Error al filtrar por workflow ${workflow}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Obtiene estadísticas de configuraciones WhatsApp
 */
export const getCommerceWhatsAppStatsService = async () => {
  try {
    const result = await getCommerceWhatsAppService();

    if (!result.success) {
      return result;
    }

    const data = result.data;

    const total = data.length;
    const activos = data.filter((item) => item.activo === true).length;
    const inactivos = data.filter((item) => item.activo === false).length;
    const conPulso = data.filter((item) => item.pulso === true).length;
    const sinPulso = data.filter((item) => item.pulso === false).length;
    const conTerminal = data.filter(
      (item) => item.idTerminal !== null && item.idTerminal !== undefined && item.idTerminal !== 0
    ).length;
    const sinTerminal = data.filter(
      (item) => item.idTerminal === null || item.idTerminal === undefined || item.idTerminal === 0
    ).length;
    const conNombreTerminal = data.filter(
      (item) =>
        item.terminalName && item.terminalName !== "Sin terminal" && item.terminalName !== null
    ).length;

    const gruposNOC = [...new Set(data.map((item) => item.grupoAlertaNOC).filter(Boolean))];
    const workflows = [...new Set(data.map((item) => item.workflow).filter(Boolean))];
    const terminalNames = [...new Set(data.map((item) => item.terminalName).filter(Boolean))];

    const gruposNOCCounts = {};
    data.forEach((item) => {
      const grupo = item.grupoAlertaNOC || "Sin grupo";
      gruposNOCCounts[grupo] = (gruposNOCCounts[grupo] || 0) + 1;
    });

    const workflowsCounts = {};
    data.forEach((item) => {
      const workflow = item.workflow || "Sin workflow";
      workflowsCounts[workflow] = (workflowsCounts[workflow] || 0) + 1;
    });

    const terminalNamesCounts = {};
    data.forEach((item) => {
      const terminal = item.terminalName || "Sin terminal";
      terminalNamesCounts[terminal] = (terminalNamesCounts[terminal] || 0) + 1;
    });

    return {
      success: true,
      data: {
        total,
        activos,
        inactivos,
        conPulso,
        sinPulso,
        conTerminal,
        sinTerminal,
        conNombreTerminal,
        gruposNOC: gruposNOC.length,
        workflows: workflows.length,
        terminalNames: terminalNames.length,
        gruposNOCList: gruposNOC,
        workflowsList: workflows,
        terminalNamesList: terminalNames,
        gruposNOCCounts,
        workflowsCounts,
        terminalNamesCounts,
      },
      message: "Estadísticas de WhatsApp obtenidas exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener estadísticas de WhatsApp:", error);
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
};

/**
 * Valida si un workflow es de tipo J.A.R.V.I.S INTEGRACION (con variantes)
 * @param {string} workflowName - Nombre del workflow
 * @returns {boolean}
 */
const isJarvisIntegrationWorkflow = (workflowName) => {
  if (!workflowName) return false;

  // Versión flexible que detecta cualquier variante de J.A.R.V.I.S
  return (
    workflowName.includes("J.A.R.V.I.S") ||
    workflowName === "J.A.R.V.I.S INTEGRACION" ||
    workflowName === "J.A.R.V.I.S. INTEGRACIÓN" ||
    workflowName.includes("JARVIS")
  );
};

/**
 * Prepara el payload para crear/actualizar configuración manejando terminal nula para J.A.R.V.I.S
 * @param {Object} configData - Datos de la configuración
 * @returns {Object} Payload preparado
 */
const preparePayload = (configData) => {
  const isJarvis = isJarvisIntegrationWorkflow(configData.nameworkflows);

  // Determinar si se debe enviar terminal nula
  let idterminal;
  if (isJarvis && !configData.idterminal) {
    idterminal = null;
  } else {
    idterminal = configData.idterminal ? parseInt(configData.idterminal, 10) : 0;
  }

  return {
    status: configData.status === true,
    idcommerce: parseInt(configData.idcommerce, 10),
    idterminal: idterminal,
    terminalname: configData.terminalname || "",
    namecommerce: configData.namecommerce,
    groupnoc: configData.groupnoc,
    groupName: configData.groupName || "",
    chatId: configData.chatId || "",
    nameworkflows: configData.nameworkflows,
    pulse: configData.pulse === true,
  };
};

/**
 * Valida los campos requeridos según el tipo de workflow
 * @param {Object} configData - Datos de la configuración
 * @returns {Object} Resultado de la validación
 */
const validateRequiredFields = (configData) => {
  // Siempre requeridos
  if (!configData.idcommerce) {
    return { valid: false, error: "El ID del comercio es requerido" };
  }
  if (!configData.groupnoc) {
    return { valid: false, error: "El grupo NOC es requerido" };
  }
  if (!configData.nameworkflows) {
    return { valid: false, error: "El workflow es requerido" };
  }

  const isJarvis = isJarvisIntegrationWorkflow(configData.nameworkflows);

  // Terminal es requerida solo si NO es J.A.R.V.I.S
  if (!isJarvis && !configData.idterminal) {
    return { valid: false, error: "La terminal es requerida para este workflow" };
  }

  return { valid: true };
};

/**
 * Crea una nueva configuración de WhatsApp (con soporte para terminal nula en J.A.R.V.I.S)
 * @param {Object} configData - Datos de la configuración a crear
 */
export const createCommerceWhatsAppService = async (configData) => {
  try {
    // Validar campos requeridos
    const validation = validateRequiredFields(configData);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    const payload = preparePayload(configData);

    const { data } = await axiosInstance.post(
      "/api/automatization/jarvis/commerce-whatsapp/create-commerce",
      payload
    );

    if (!data || typeof data !== "object") {
      return {
        success: false,
        uncertain: true,
        error: "Respuesta inválida del servidor",
      };
    }

    if (commerceWriteSucceeded(data)) {
      // Limpiar caché después de crear
      clearWhatsAppCache();

      return {
        success: true,
        data: data.response || data,
        message: data.msm || "Configuración creada exitosamente",
      };
    } else {
      return {
        success: false,
        uncertain: !commerceWriteRejected(data),
        error:
          data.error ||
          data.response?.error ||
          data.message ||
          data.response?.message ||
          "El servidor no confirmó la creación de la configuración",
      };
    }
  } catch (error) {
    console.error("❌ Error al crear configuración WhatsApp:", error);

    let errorMessage = "Error al crear la configuración";

    if (error.response) {
      console.error("📊 Detalles del error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });

      if (error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.msm) {
          errorMessage = error.response.data.msm;
        }
      }

      if (!errorMessage || errorMessage === "Error al crear la configuración") {
        switch (error.response.status) {
          case 400:
            errorMessage = "Datos inválidos. Verifique la información proporcionada.";
            break;
          case 409:
            errorMessage = "Conflicto al crear la configuración. Verifique los datos.";
            break;
          case 500:
            errorMessage = "Error interno del servidor. Intente más tarde.";
            break;
          default:
            errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor. Verifique su conexión.";
      console.error("🌐 Error de conexión:", error.request);
    } else {
      errorMessage = error.message;
      console.error("⚙️ Error de configuración:", error.message);
    }

    return {
      success: false,
      uncertain: !error.response || error.response.status >= 500,
      error: errorMessage,
    };
  }
};

/**
 * Actualiza una configuración de WhatsApp (con soporte para terminal nula en J.A.R.V.I.S)
 * @param {Object} configData - Datos de la configuración a actualizar
 */
export const updateCommerceWhatsAppService = async (configData) => {
  try {
    const idcommerce = parseInt(configData.idcommerce, 10);

    if (isNaN(idcommerce) || idcommerce <= 0) {
      return {
        success: false,
        error: "El ID del comercio no es válido",
      };
    }

    // Validar campos requeridos
    const validation = validateRequiredFields(configData);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    const payload = preparePayload(configData);

    const { data } = await axiosInstance.put(
      "/api/automatization/jarvis/commerce-whatsapp/update-commerce",
      payload
    );

    if (!data || typeof data !== "object") {
      return {
        success: false,
        uncertain: true,
        error: "Respuesta inválida del servidor",
      };
    }

    if (commerceWriteSucceeded(data)) {
      // Limpiar caché después de actualizar
      clearWhatsAppCache();

      return {
        success: true,
        data: data.response || data,
        message: data.msm || "Configuración actualizada exitosamente",
      };
    } else {
      return {
        success: false,
        uncertain: !commerceWriteRejected(data),
        error:
          data.error ||
          data.response?.error ||
          data.message ||
          data.response?.message ||
          "El servidor no confirmó la actualización de la configuración",
      };
    }
  } catch (error) {
    console.error("❌ Error al actualizar configuración WhatsApp:", error);

    let errorMessage = "Error al actualizar la configuración";

    if (error.response) {
      console.error("📊 Detalles del error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });

      if (error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.msm) {
          errorMessage = error.response.data.msm;
        }
      }

      if (!errorMessage || errorMessage === "Error al actualizar la configuración") {
        switch (error.response.status) {
          case 400:
            errorMessage = "Datos inválidos. Verifique la información proporcionada.";
            break;
          case 404:
            errorMessage = "La configuración no existe en el sistema.";
            break;
          case 409:
            errorMessage = "Conflicto al actualizar la configuración. Verifique los datos.";
            break;
          case 500:
            errorMessage = "Error interno del servidor. Intente más tarde.";
            break;
          default:
            errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor. Verifique su conexión.";
      console.error("🌐 Error de conexión:", error.request);
    } else {
      errorMessage = error.message;
      console.error("⚙️ Error de configuración:", error.message);
    }

    return {
      success: false,
      uncertain: !error.response || error.response.status >= 500,
      error: errorMessage,
    };
  }
};
